import type { OppSource } from '@crm/shared';
import { useEffect, useState } from 'react';

import type { ContactRow, OpportunityFromContactPayload } from '../../lib/api';
import { apiClient } from '../../lib/api';

type Props = {
  open: boolean;
  contact: ContactRow;
  onClose: () => void;
  onCreated: (opportunityId: string) => void;
};

const SOURCES: { value: OppSource; label: string }[] = [
  { value: 'recommendation', label: 'Referral' },
  { value: 'event', label: 'Event' },
  { value: 'intermediary', label: 'Intermediary' },
  { value: 'bank', label: 'Bank' },
  { value: 'lawyer', label: 'Lawyer / Notary' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'other', label: 'Other' },
];

/**
 * Modal launched from a Contact detail page (E3). The contact is the
 * authoritative party — fields it can pre-fill come from the contact record
 * (name, sector, source, country). The RM completes the financial bits.
 *
 * Note: the new opportunity starts in stage "Identified". KYC enforcement
 * for the ACTIVE transition lives in the backend route (see opportunities.routes.ts).
 */
export function NewOpportunityModal({ open, contact, onClose, onCreated }: Props) {
  const [form, setForm] = useState<OpportunityFromContactPayload>(() => prefill(contact));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(prefill(contact));
      setSubmitting(false);
      setError(null);
    }
  }, [open, contact]);

  if (!open) return null;

  function set<K extends keyof OpportunityFromContactPayload>(
    key: K,
    value: OpportunityFromContactPayload[K],
  ) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const created = await apiClient.createOpportunityForContact(form);
      onCreated(created.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="New opportunity"
      className="fixed inset-0 z-40 bg-graphite/40 backdrop-blur-[2px] flex items-start lg:items-center justify-center overflow-y-auto"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-2xl bg-surface rounded-card shadow-panel m-4 lg:m-10"
      >
        <header className="px-6 py-4 border-b border-platinum">
          <p className="label-uppercase">— New opportunity</p>
          <h2 className="mt-1 font-display text-xl font-semibold">
            For {contact.first_name} {contact.last_name}
          </h2>
          {contact.company && <p className="text-xs text-ink-tertiary mt-1">{contact.company}</p>}
        </header>

        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <Field label="Opportunity name *" full>
            <input
              required
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className="input-base"
            />
          </Field>

          <Field label="Domicile country (ISO2) *">
            <input
              required
              maxLength={2}
              value={form.domiciled_country}
              onChange={(e) => set('domiciled_country', e.target.value.toUpperCase())}
              className="input-base uppercase font-tabular"
            />
          </Field>
          <Field label="Sector *">
            <input
              required
              value={form.industry_sector}
              onChange={(e) => set('industry_sector', e.target.value)}
              className="input-base"
            />
          </Field>

          <Field label="Total wealth (CHF) *">
            <input
              required
              type="number"
              step="100000"
              min={0}
              value={form.total_wealth}
              onChange={(e) => set('total_wealth', Number(e.target.value))}
              className="input-base font-tabular text-right"
            />
          </Field>
          <Field label="Initial expected (CHF) *">
            <input
              required
              type="number"
              step="100000"
              min={0}
              value={form.initial_expected_amount}
              onChange={(e) => set('initial_expected_amount', Number(e.target.value))}
              className="input-base font-tabular text-right"
            />
          </Field>

          <Field label="Potential N+1 (CHF)">
            <input
              type="number"
              step="100000"
              min={0}
              value={form.potential_expected_amount_y1 ?? 0}
              onChange={(e) => set('potential_expected_amount_y1', Number(e.target.value))}
              className="input-base font-tabular text-right"
            />
          </Field>
          <Field label="RoA (bps) *">
            <input
              required
              type="number"
              step="1"
              min={0}
              max={500}
              value={form.roa_bps}
              onChange={(e) => set('roa_bps', Number(e.target.value))}
              className="input-base font-tabular text-right"
            />
          </Field>

          <Field label="Existing bank">
            <input
              value={form.existing_bank ?? ''}
              onChange={(e) => set('existing_bank', e.target.value || undefined)}
              className="input-base"
            />
          </Field>
          <Field label="Expected timeframe">
            <input
              value={form.expected_timeframe ?? ''}
              onChange={(e) => set('expected_timeframe', e.target.value || undefined)}
              placeholder="e.g. Q3 2026"
              className="input-base"
            />
          </Field>

          <Field label="Source *">
            <select
              value={form.source}
              onChange={(e) => set('source', e.target.value as OppSource)}
              className="select-base"
            >
              {SOURCES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="PEP?">
            <label className="flex items-center gap-2 mt-2 text-sm">
              <input
                type="checkbox"
                checked={form.pep_flag ?? false}
                onChange={(e) => set('pep_flag', e.target.checked)}
              />
              Politically Exposed Person
            </label>
          </Field>

          {error && (
            <p className="md:col-span-2 text-sm text-semantic-error border border-signal/30 bg-bg-alert px-3 py-2 rounded-card">
              {error}
            </p>
          )}

          <p className="md:col-span-2 mt-2 text-xs text-ink-tertiary leading-relaxed">
            The opportunity starts in stage <strong>Identified</strong>. The transition to{' '}
            <strong>Active</strong> is blocked until KYC is validated (FINMA + SEC rule).
          </p>
        </div>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-platinum">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60">
            {submitting ? 'Creating…' : 'Create opportunity'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function prefill(contact: ContactRow): OpportunityFromContactPayload {
  // Default values derived from the contact + sensible defaults (CLAUDE.md
  // pipeline rules). The RM still has to confirm wealth / amounts.
  const baseAmount =
    contact.wealth_tranche === 'over_100m'
      ? 25_000_000
      : contact.wealth_tranche === 'from_25m_to_100m'
        ? 15_000_000
        : contact.wealth_tranche === 'from_5m_to_25m'
          ? 8_000_000
          : contact.wealth_tranche === 'from_1m_to_5m'
            ? 2_500_000
            : 1_000_000;
  return {
    contact_id: contact.id,
    name: contact.company
      ? `${contact.company} — ${contact.last_name}`
      : `${contact.first_name} ${contact.last_name}`,
    domiciled_country: (contact.addresses[0]?.country ?? contact.tax_residence ?? 'CH').slice(0, 2),
    industry_sector: contact.industry_sector ?? 'Other',
    total_wealth: baseAmount * 3,
    initial_expected_amount: baseAmount,
    potential_expected_amount_y1: Math.round(baseAmount * 1.5),
    roa_bps: 90,
    source: contact.source,
    pep_flag: contact.pep_flag,
  };
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={'block text-sm ' + (full ? 'md:col-span-2' : '')}>
      <span className="block text-xs uppercase tracking-wider text-ink-secondary mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
